/**
 * MembershipFeeSection — the Jäsenmaksu in-page view (T028).
 *
 * Composes the demo Jäsenmaksu surface, grounded in the proto (Jasenmaksut.tsx):
 *   - The latest invoice card. Every card shows the account number, bank,
 *     reference (with copy buttons) and invoice date its invoice carries
 *     (D-252(a), D-PD-014); billing method on the latest invoice only.
 *   - A "Maksa" (pay) action that initiates a Paytrail payment (044). On success
 *     the port returns { kind: 'redirect', redirectUrl } and the browser
 *     FULL-PAGE navigates to the Paytrail hosted page; on a typed failure it
 *     returns { kind: 'error', error } and the section renders the HONEST state
 *     for that arm (contract §B) — never a fabricated success (Constitution P7).
 *   - The e-laskuehdotus surface (delivery method + request flow, 048).
 *   - The paid invoice history.
 *
 * Consumes the membershipBilling port via Suspense (useMembershipBilling caches
 * the reads); mount inside a Suspense boundary. The per-page "demo data" notice
 * was removed (item-4 r1 C1).
 *
 * Copy-to-clipboard uses the same silent-failure posture as MemberProfileCard
 * (RD-006): a success swaps the row's icon to a checkmark briefly; a rejection
 * leaves the button as-is.
 *
 * No raw colour literals (FR-013) — token classes only. Mobile-first.
 *
 * Owned by feature 026-proto-convergence-v2.
 * Task: T028
 */

import { type JSX, useEffect, useRef, useState } from 'react';
import type { MessageKeys } from '../../i18n/messages/index.js';
import { useLanguage } from '../../i18n/useLanguage.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import { useCapabilityAllowed } from '../../membership/useCapability.js';
import type { PaymentInitiationError } from '../../membership-billing/MembershipBillingPort.js';
import { useMembershipBilling } from '../../membership-billing/useMembershipBilling.js';
import { EInvoiceProposalRequest } from './EInvoiceProposalRequest.js';
import { DeliveryMethodCard, InvoiceCard } from './InvoiceCard.js';
import { ProfileSectionHeader } from './ProfileSectionHeader.js';

// Honest copy per initiate-payment error arm (044 contract §B). MISCONFIGURED is
// an "unavailable" note (and the pay action is disabled — no fabricated success);
// NOT_PAYABLE is a neutral, non-disclosing message; READ_UNAVAILABLE /
// PROVIDER_UNAVAILABLE are a "couldn't start payment — try again" notice (retry
// stays allowed — the button is NOT disabled).
const PAY_ERROR_MESSAGE_KEY: Record<PaymentInitiationError, MessageKeys> = {
  MISCONFIGURED: 'profile.fee.payUnavailable',
  NOT_PAYABLE: 'profile.fee.payNotPayable',
  READ_UNAVAILABLE: 'profile.fee.payRetry',
  PROVIDER_UNAVAILABLE: 'profile.fee.payRetry',
};

// The back affordance is owned by the route (item-4 r1 C5), so this section
// takes no props — it renders only its content + subordinate heading.
export function MembershipFeeSection(): JSX.Element {
  const t = useTranslate();
  const { active: locale } = useLanguage();
  const { invoices, billingFacts, requestEInvoiceProposal, refetch, initiatePayment } =
    useMembershipBilling();
  // Membership-state carve-out (028-membership-state-gating, FR-008): a not-valid
  // member may VIEW the latest invoice read-only but has no in-service payment.
  // The invoice details (InvoiceCard) stay; only the pay action is gated off.
  const canPayInService = useCapabilityAllowed('pay_invoice_in_service');

  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  // Non-null only after a FAILED initiate-payment attempt; a successful attempt
  // full-page navigates away, so there is no "success" state to hold here.
  const [payError, setPayError] = useState<PaymentInitiationError | null>(null);
  // True while an initiate-payment request is in flight — drives the button's
  // `aria-busy` so the async transition is not silent to assistive tech (a
  // successful attempt navigates away; a failure clears it).
  const [isPaying, setIsPaying] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const payBlockRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) {
        clearTimeout(copyTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // On the terminal MISCONFIGURED arm the pay button self-disables; move focus
    // to the pay block so keyboard focus is not orphaned on <body>. The error copy
    // itself is announced by the role=status live region below — we focus the
    // wrapping container, not the status node, so it is not read twice.
    if (payError === 'MISCONFIGURED') {
      payBlockRef.current?.focus();
    }
  }, [payError]);

  // Newest-first per the port contract; the first invoice is the latest.
  const [latest, ...history] = invoices;

  const handleCopy = (value: string, key: string): void => {
    if (typeof navigator === 'undefined' || navigator.clipboard === undefined) {
      return;
    }
    navigator.clipboard.writeText(value).then(
      () => {
        setCopiedKey(key);
        if (copyTimerRef.current !== null) {
          clearTimeout(copyTimerRef.current);
        }
        copyTimerRef.current = setTimeout(() => {
          setCopiedKey(null);
          copyTimerRef.current = null;
        }, 2000);
      },
      () => {
        // Silent failure (RD-006) — the row keeps its copy icon.
      },
    );
  };

  const handlePay = (): void => {
    if (latest === undefined) {
      return;
    }
    // 044: initiate a Paytrail payment. On success the port returns a redirect
    // URL and we FULL-PAGE navigate the browser to the Paytrail hosted page
    // (it is an EXTERNAL site — a react-router nav would not leave the SPA).
    // On a typed failure we surface the honest state for that arm (contract §B)
    // and never claim a payment happened (P7). A rejected promise (transport
    // error) is treated as PROVIDER_UNAVAILABLE — the honest "try again" arm.
    setPayError(null);
    setIsPaying(true);
    void Promise.resolve(initiatePayment(latest.id))
      .then((result) => {
        if (result.kind === 'redirect') {
          // Full-page navigation is imminent — leave `isPaying` true so the button
          // stays busy until the browser leaves the SPA.
          window.location.assign(result.redirectUrl);
          return;
        }
        setIsPaying(false);
        setPayError(result.error);
      })
      .catch(() => {
        setIsPaying(false);
        setPayError('PROVIDER_UNAVAILABLE');
      });
  };

  return (
    <div className="flex flex-col gap-4">
      <ProfileSectionHeader title={t('profile.fee.heading')} />

      {/* 041: honest empty state — a known member with no invoices on record
          (not an error, not demo data). The e-invoice affordance still shows. */}
      {invoices.length === 0 && (
        <p className="text-sm text-muted-foreground" role="status">
          {t('profile.fee.empty')}
        </p>
      )}

      {/* ONE instance whatever the host: a re-read that moves the invoice list
          between empty and non-empty must not remount it, or the per-bank
          idempotency keys and the outcome message would be lost. */}
      <EInvoiceProposalRequest
        facts={billingFacts}
        onRequest={requestEInvoiceProposal}
        onSettled={refetch}
        locale={locale}
      >
        {({ methodLabel, action, methodRef }) =>
          latest !== undefined ? (
            <InvoiceCard
              invoice={latest}
              locale={locale}
              onCopy={handleCopy}
              copiedKey={copiedKey}
              // The proto's placement: Laskutustapa is the LATEST invoice's last
              // row, with the switch affordance inline on it.
              deliveryMethod={{ label: methodLabel, action, labelRef: methodRef }}
            />
          ) : (
            // No invoice to host the row: the billing method and the request
            // option still apply (spec edge case, 048), so the row stands alone.
            <DeliveryMethodCard label={methodLabel} action={action} labelRef={methodRef} />
          )
        }
      </EInvoiceProposalRequest>

      {/* Pay CTA (041 FR-009): the `pay_invoice_in_service` capability is the
          master switch (028) — while it is disabled no CTA shows, so a payable
          invoice never surfaces a dead/deferred action. On the real path the CTA
          additionally requires the back office to mark the invoice payable
          (`payable !== false`; demo invoices leave it unset → shown). */}
      {latest !== undefined &&
        latest.status === 'unpaid' &&
        latest.payable !== false &&
        // 044 US3: a fresh optimistic Paytrail payment makes the invoice
        // functionally paid — suppress the pay CTA (no double payment).
        latest.paidLabel !== 'optimistic-paid' &&
        canPayInService && (
          <div className="flex flex-col gap-2" ref={payBlockRef} tabIndex={-1}>
            {/* Pay CTA (044). Disabled ONLY for the MISCONFIGURED arm — an honest
                "unavailable" state with no fabricated success (contract §B). The
                retry arms (READ_UNAVAILABLE / PROVIDER_UNAVAILABLE) keep the
                button enabled so the member can try again; NOT_PAYABLE renders a
                neutral message and leaves the button as-is. On success the click
                full-page navigates to Paytrail, so no local success state.
                `aria-busy` marks the in-flight request so the async action is not
                silent to assistive tech. */}
            <button
              type="button"
              onClick={handlePay}
              disabled={payError === 'MISCONFIGURED'}
              aria-busy={isPaying}
              className="rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
            >
              {t('profile.fee.payCta')}
            </button>
            {payError !== null && (
              <div
                className="rounded-lg bg-muted p-3 text-sm text-muted-foreground"
                role="status"
                aria-live="polite"
                aria-atomic="true"
              >
                {t(PAY_ERROR_MESSAGE_KEY[payError])}
              </div>
            )}
          </div>
        )}

      {history.length > 0 && (
        <div className="flex flex-col gap-3">
          <h4 className="text-base font-semibold text-foreground">
            {t('profile.fee.paymentHistory')}
          </h4>
          {history.map((invoice) => (
            <InvoiceCard
              key={invoice.id}
              invoice={invoice}
              locale={locale}
              onCopy={handleCopy}
              copiedKey={copiedKey}
            />
          ))}
        </div>
      )}
    </div>
  );
}
